/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createEntityAdapter } from '@reduxjs/toolkit';

import { Instance } from '../../../../ts/instance';

/**
 * This module contains an entity adapter that encapsulates a list of entities (in this case, instances) and
 * provides several selectors to retrieve said entities.
 */

/**
 * Generates an instance identifier based on a region and an instance ID.
 * It can be called with either an "instance" object (in which case it will concatenate the "region" and "instance_id"
 * properties) or with a "session" object (in which case it will concatenate the "region" and "instance" properties).
 *
 * @param {Object} instanceOrSession The object that contains region and instance ID information.
 * @return {string} A string that joins region and instance ID information to generate a unique instance identifier.
 */
export const formatInstanceId = (instanceOrSession: {
  region?: string;
  instance_id?: string;
  instance?: string;
}): string =>
  instanceOrSession.region +
  ':' +
  (instanceOrSession.instance_id || instanceOrSession.instance);

export const sortComparer = (
  instance1: Instance,
  instance2: Instance
): number => instance1.name.toLowerCase().localeCompare(instance2.name);

/**
 * Generates an entity adapter that contains instances and exposes selectors to retrieve the data (where the
 * list of instances is sorted by instance name in a case-insensitive manner
 */
export const adapter = createEntityAdapter<Instance>({
  selectId: instance => formatInstanceId(instance),
  sortComparer
});
